import { useCallback, useEffect, useRef, type ReactNode } from 'react';

/**
 * Recursive resizable split layout.
 *
 * The important detail is what happens during a drag: nothing goes through
 * React. `pointermove` writes `flexBasis` straight onto the two affected
 * children and returns. Committing to state on every move would re-render the
 * entire panel tree — including every canvas wrapper — sixty times a second
 * while the user is dragging, which is exactly when the app must stay smooth.
 * State is updated once, on release, so the layout can be persisted.
 */

export type LayoutNode =
  | { type: 'leaf'; key: string }
  | { type: 'split'; dir: 'row' | 'col'; sizes: number[]; children: LayoutNode[] };

export interface SplitGridProps {
  layout: LayoutNode;
  onChange: (next: LayoutNode) => void;
  render: (key: string) => ReactNode;
}

/** Minimum panel extent in pixels, so nothing can be dragged to zero. */
const MIN_PX = 140;
const DIVIDER = 5;

export function SplitGrid({ layout, onChange, render }: SplitGridProps) {
  return <SplitNode node={layout} onChange={onChange} render={render} />;
}

function SplitNode({
  node,
  onChange,
  render,
}: {
  node: LayoutNode;
  onChange: (next: LayoutNode) => void;
  render: (key: string) => ReactNode;
}) {
  if (node.type === 'leaf') {
    return <div className="split-leaf">{render(node.key)}</div>;
  }
  return <Split node={node} onChange={onChange} render={render} />;
}

function Split({
  node,
  onChange,
  render,
}: {
  node: Extract<LayoutNode, { type: 'split' }>;
  onChange: (next: LayoutNode) => void;
  render: (key: string) => ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const childRefs = useRef<(HTMLDivElement | null)[]>([]);
  const drag = useRef<{
    index: number;
    startPos: number;
    total: number;
    a0: number;
    b0: number;
    live: number[];
  } | null>(null);

  const horizontal = node.dir === 'row';

  // Keep the live DOM in step when the layout prop changes from outside a drag
  // (reset button, a restored layout).
  useEffect(() => {
    childRefs.current.forEach((el, i) => {
      if (el) el.style.flexBasis = `${node.sizes[i]}%`;
    });
  }, [node.sizes]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>, index: number) => {
      const container = containerRef.current;
      if (!container) return;
      e.preventDefault();
      // Capture keeps the drag alive when the pointer outruns the 5px divider,
      // but it must never be load-bearing: it throws for a pointer id the
      // browser does not consider active, and an exception here would abort
      // the handler before the drag state exists, leaving resize dead.
      try {
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        /* fall back to plain move events */
      }

      const rect = container.getBoundingClientRect();
      const total = horizontal ? rect.width : rect.height;
      drag.current = {
        index,
        startPos: horizontal ? e.clientX : e.clientY,
        total,
        a0: node.sizes[index],
        b0: node.sizes[index + 1],
        live: [...node.sizes],
      };
      document.body.classList.add(horizontal ? 'resizing-col' : 'resizing-row');
    },
    [horizontal, node.sizes],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const d = drag.current;
      if (!d || d.total <= 0) return;

      const pos = horizontal ? e.clientX : e.clientY;
      const deltaPct = ((pos - d.startPos) / d.total) * 100;
      const minPct = (MIN_PX / d.total) * 100;

      // A divider only ever redistributes between its two neighbours; the rest
      // of the row keeps its size, which is what makes nested layouts stable.
      let a = d.a0 + deltaPct;
      let b = d.b0 - deltaPct;
      if (a < minPct) {
        b -= minPct - a;
        a = minPct;
      }
      if (b < minPct) {
        a -= minPct - b;
        b = minPct;
      }
      if (a < minPct || b < minPct) return;

      d.live[d.index] = a;
      d.live[d.index + 1] = b;

      const ea = childRefs.current[d.index];
      const eb = childRefs.current[d.index + 1];
      if (ea) ea.style.flexBasis = `${a}%`;
      if (eb) eb.style.flexBasis = `${b}%`;
    },
    [horizontal],
  );

  const endDrag = useCallback(() => {
    const d = drag.current;
    drag.current = null;
    document.body.classList.remove('resizing-col', 'resizing-row');
    if (!d) return;
    onChange({ ...node, sizes: d.live });
  }, [node, onChange]);

  const replaceChild = useCallback(
    (i: number, child: LayoutNode) => {
      const children = [...node.children];
      children[i] = child;
      onChange({ ...node, children });
    },
    [node, onChange],
  );

  return (
    <div className={`split split-${node.dir}`} ref={containerRef}>
      {node.children.map((child, i) => (
        <div
          key={child.type === 'leaf' ? child.key : `split-${i}`}
          className="split-pane"
          ref={(el) => {
            childRefs.current[i] = el;
          }}
          style={{ flexBasis: `${node.sizes[i]}%` }}
        >
          <SplitNode node={child} onChange={(next) => replaceChild(i, next)} render={render} />
          {i < node.children.length - 1 && (
            <div
              className={`divider divider-${node.dir}`}
              style={horizontal ? { width: DIVIDER } : { height: DIVIDER }}
              onPointerDown={(e) => onPointerDown(e, i)}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              onDoubleClick={() => {
                // Double-click equalises the two neighbours.
                const sizes = [...node.sizes];
                const sum = sizes[i] + sizes[i + 1];
                sizes[i] = sum / 2;
                sizes[i + 1] = sum / 2;
                onChange({ ...node, sizes });
              }}
              title="Drag to resize · double-click to even out"
            />
          )}
        </div>
      ))}
    </div>
  );
}
